import React, { useState, useEffect, useCallback } from 'react';
import { ArrowRight, Maximize2, Minimize2 } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import DownloadIcon from '../../assets/download.svg';
import Example from '../../assets/Ambientación de ejemplo.pdf';
import Form from '../Form';
import CustomMap from '../CustomMap';
import { ToastProvider } from 'react-toast-notifications';
import { useStore } from '../../store';
import { StepIndicator } from '../ui/StepIndicator';
import { cn } from '../../lib/cn';
import { trackFunnelStep, FunnelSteps } from '../../lib/analytics';

const STEPS = ['Marcá tu lote', 'Tus datos', 'Recibí el reporte'];

function scrollToFormFields() {
  const input = document.getElementById('nombre');
  if (!input) return;

  setTimeout(() => {
    input.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(() => input.focus(), 400);
  }, 300);
}

const RentMap = () => {
  const [{ map }] = useStore();
  const [currentStep, setCurrentStep] = useState(1);
  const [mapExpanded, setMapExpanded] = useState(false);
  const [pendingContinue, setPendingContinue] = useState(false);

  useEffect(() => {
    if (map && map.gis) {
      setCurrentStep(2);
    } else {
      setCurrentStep(1);
    }
  }, [map]);

  useEffect(() => {
    if (!mapExpanded) {
      setPendingContinue(false);
    }
  }, [mapExpanded]);

  useEffect(() => {
    if (!map?.gis) {
      setPendingContinue(false);
    }
  }, [map?.gis]);

  const collapseAndGoToForm = useCallback((location) => {
    trackFunnelStep(FunnelSteps.MAP_COLLAPSED, { location });
    setPendingContinue(false);
    setMapExpanded(false);
    setCurrentStep(2);
    scrollToFormFields();
  }, []);

  const handlePolygonConfirmed = useCallback(() => {
    setCurrentStep(2);

    if (mapExpanded) {
      setPendingContinue(true);
    }
  }, [mapExpanded]);

  const handleContinueToForm = useCallback(() => {
    collapseAndGoToForm('continue_cta');
  }, [collapseAndGoToForm]);

  const toggleMapExpanded = useCallback(() => {
    setMapExpanded((prev) => {
      const next = !prev;
      trackFunnelStep(next ? FunnelSteps.MAP_EXPANDED : FunnelSteps.MAP_COLLAPSED, {
        location: 'map_toolbar',
      });
      return next;
    });
  }, []);

  useEffect(() => {
    if (!mapExpanded) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        trackFunnelStep(FunnelSteps.MAP_COLLAPSED, { location: 'escape_key' });
        setMapExpanded(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [mapExpanded]);

  return (
    <ToastProvider>
      <div className={cn('p-3 md:p-6', mapExpanded && 'p-0')}>
        {!mapExpanded && <StepIndicator steps={STEPS} currentStep={currentStep} />}

        <div
          className={cn(
            'grid gap-4 lg:grid-cols-2 lg:items-stretch lg:gap-6',
            mapExpanded && 'block',
          )}
        >
          <div
            className={cn(
              'flex min-h-0 flex-col overflow-hidden rounded-xl border border-brand-dark/10 bg-surface-muted',
              mapExpanded &&
                'fixed inset-0 z-[300] m-0 flex h-[100dvh] w-full flex-col rounded-none border-0 bg-white',
            )}
          >
            <div className="flex shrink-0 items-start justify-between gap-3 border-b border-brand-dark/5 bg-white px-3 py-3 md:px-4">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-brand-dark">Paso 1 — Marcá tu lote en el mapa</p>
                <p className="text-xs text-brand-earth">
                  Buscá tu campo y dibujá un lote en el mapa o subí un archivo KML/KMZ del lote (5–100 ha)
                </p>
              </div>
              <button
                type="button"
                onClick={toggleMapExpanded}
                className={cn(
                  'flex shrink-0 items-center gap-1.5 rounded-lg border border-brand-dark/10 bg-white px-2.5 py-2 text-xs font-semibold text-brand-dark transition hover:border-brand-accent hover:text-brand-accent',
                  mapExpanded && 'border-brand-accent/30 bg-brand-accent/10',
                )}
                title={mapExpanded ? 'Volver al formulario' : 'Expandir mapa'}
                aria-label={mapExpanded ? 'Volver al formulario' : 'Expandir mapa'}
                data-cta={mapExpanded ? 'map-minimize' : 'map-expand'}
              >
                {mapExpanded ? (
                  <>
                    <Minimize2 className="h-4 w-4" aria-hidden />
                    <span className="hidden sm:inline">Volver</span>
                  </>
                ) : (
                  <>
                    <Maximize2 className="h-4 w-4" aria-hidden />
                    <span className="hidden sm:inline">Expandir</span>
                  </>
                )}
              </button>
            </div>

            <div
              id="rent-map-container"
              className={cn(
                'relative min-h-[420px] flex-1 sm:min-h-[480px] lg:min-h-[320px]',
                mapExpanded && 'rent-map-container--expanded min-h-0 sm:min-h-0 lg:min-h-0',
              )}
            >
              <CustomMap
                isExpanded={mapExpanded}
                onPolygonConfirmed={handlePolygonConfirmed}
              />
            </div>

            <AnimatePresence>
              {mapExpanded && pendingContinue && (
                <motion.button
                  type="button"
                  initial={{ opacity: 0, y: 20, scale: 0.92 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 12, scale: 0.95 }}
                  transition={{ type: 'spring', stiffness: 420, damping: 28 }}
                  onClick={handleContinueToForm}
                  className="fixed bottom-[calc(3.75rem+env(safe-area-inset-bottom,0px))] right-4 z-[310] flex items-center gap-2 rounded-full bg-brand-accent px-5 py-3.5 text-sm font-bold text-brand-dark shadow-lg ring-2 ring-white/80 transition hover:bg-brand-accent/90 active:scale-[0.98] lg:hidden"
                  data-cta="map-continue-to-form"
                  aria-label="Continuar al formulario"
                >
                  Continuar
                  <ArrowRight className="h-4 w-4" aria-hidden />
                </motion.button>
              )}
            </AnimatePresence>

            {!mapExpanded ? (
              <a
                href={Example}
                target="_blank"
                rel="noopener noreferrer"
                className="flex shrink-0 items-center gap-2 px-3 py-3 text-sm font-medium text-brand-accent hover:underline md:px-4"
              >
                Ver ambientación de ejemplo
                <img src={DownloadIcon} alt="" className="h-4 w-4" />
              </a>
            ) : (
              <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-brand-dark/10 bg-white px-3 py-2.5 md:px-4">
                {pendingContinue ? (
                  <>
                    <p className="text-xs font-medium text-brand-dark">
                      Lote confirmado — continuá para completar tus datos
                    </p>
                    <button
                      type="button"
                      onClick={handleContinueToForm}
                      className="flex items-center gap-1.5 rounded-lg bg-brand-accent px-4 py-2 text-xs font-bold text-brand-dark"
                      data-cta="map-continue-to-form-footer"
                    >
                      Continuar
                      <ArrowRight className="h-3.5 w-3.5" aria-hidden />
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      trackFunnelStep(FunnelSteps.MAP_COLLAPSED, { location: 'map_footer' });
                      setMapExpanded(false);
                    }}
                    className="rounded-lg bg-brand-dark px-4 py-2 text-xs font-semibold text-white hover:bg-brand-dark/90"
                  >
                    Volver al formulario
                  </button>
                )}
              </div>
            )}
          </div>

          <div
            className={cn(
              'flex flex-col rounded-xl border border-brand-dark/10 bg-surface-muted',
              mapExpanded && 'hidden',
            )}
          >
            <Form onStepChange={setCurrentStep} />
          </div>
        </div>
      </div>
    </ToastProvider>
  );
};

export default RentMap;
